import React, { useEffect, useState, Component } from 'react';
import { EyeIcon, Loader2 } from 'lucide-react';
import { FormSection, TextField, TextAreaField, FileUploadField } from '../FormComponents';
import { DataTable } from '../DataTable';
import { StatusBadge } from '../StatusBadge';
import { SupplierViewModal } from './SupplierViewModal';
import { EOISupplier } from '../../types';
const mockEOISuppliers: EOISupplier[] = [{
  id: '1',
  supplierId: 'SUP-001',
  supplierName: 'TechCorp Solutions',
  submissionDate: '2023-10-15',
  status: 'Approved',
  documents: ['company_profile.pdf', 'financial_statement.pdf']
}, {
  id: '2',
  supplierId: 'SUP-002',
  supplierName: 'Global Logistics Inc',
  submissionDate: '2023-10-16',
  status: 'Pending',
  documents: ['eoi_submission.pdf']
}];
interface EOIPanelProps {
  onStatusChange?: (status: string) => void;
}
export function EOIPanel({
  onStatusChange
}: EOIPanelProps) {
  const [formData, setFormData] = useState({
    submissionDeadline: '',
    evaluationCriteria: '',
    remarks: '',
    status: 'Draft'
  });
  const [selectedSupplier, setSelectedSupplier] = useState<EOISupplier | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isReadOnly = formData.status !== 'Draft';
  useEffect(() => {
    if (onStatusChange) {
      onStatusChange(formData.status);
    }
  }, [formData.status, onStatusChange]);
  const columns = [{
    key: 'supplierName',
    label: 'Supplier Name'
  }, {
    key: 'submissionDate',
    label: 'Submission Date'
  }, {
    key: 'status',
    label: 'Status',
    render: (item: EOISupplier) => <StatusBadge status={item.status} data-id="element-263" />
  }];
  const handleViewSupplier = (supplier: EOISupplier) => {
    setSelectedSupplier(supplier);
    setIsModalOpen(true);
  };
  const handleSubmit = () => {
    setIsSubmitting(true);
    setFormData({
      ...formData,
      status: 'Pending'
    });
    // Simulate approval process
    setTimeout(() => {
      setFormData(prev => ({
        ...prev,
        status: 'Approved'
      }));
      setIsSubmitting(false);
    }, 1500);
  };
  const handlePublish = () => {
    setFormData({
      ...formData,
      status: 'Published'
    });
  };
  return <div className="space-y-6" data-id="element-264">
      <div className="bg-white p-6 rounded-lg border border-gray-200" data-id="element-265">
        <FormSection title="Expression of Interest (EOI) Details" data-id="element-266">
          <TextField label="EOI Submission Deadline" type="date" value={formData.submissionDeadline} onChange={e => setFormData({
          ...formData,
          submissionDeadline: e.target.value
        })} disabled={isReadOnly} data-id="element-267" />
          <div className="md:col-span-2" data-id="element-268">
            <TextAreaField label="EOI Evaluation Criteria" value={formData.evaluationCriteria} onChange={e => setFormData({
            ...formData,
            evaluationCriteria: e.target.value
          })} rows={3} placeholder="e.g., experience, financial capacity, technical capability" disabled={isReadOnly} data-id="element-269" />
          </div>
          <FileUploadField label="EOI Attachment" disabled={isReadOnly} data-id="element-270" />
          <div className="md:col-span-2" data-id="element-271">
            <TextAreaField label="Remarks" value={formData.remarks} onChange={e => setFormData({
            ...formData,
            remarks: e.target.value
          })} rows={2} disabled={isReadOnly} data-id="element-272" />
          </div>
        </FormSection>

        <div className="mt-8" data-id="element-273">
          <h3 className="text-lg font-medium text-gray-900 mb-4 pb-2 border-b border-gray-200" data-id="element-274">
            Supplier Submissions
          </h3>
          <div className="h-64" data-id="element-275">
            <DataTable columns={columns} data={mockEOISuppliers} keyExtractor={item => item.id} actions={item => <button type="button" onClick={() => handleViewSupplier(item)} className="text-indigo-600 hover:text-indigo-900 flex items-center justify-end w-full" data-id="element-277">
                  <EyeIcon className="h-4 w-4 mr-1" data-id="element-278" /> View
                </button>} data-id="element-276" />
          </div>
        </div>

        <div className="mt-8 pt-5 border-t border-gray-200 flex items-center justify-between" data-id="element-279">
          <div className="flex items-center" data-id="element-280">
            <span className="text-sm font-medium text-gray-500 mr-2" data-id="element-281">
              Status:
            </span>
            <StatusBadge status={formData.status} data-id="element-282" />
          </div>
          <div className="flex space-x-3" data-id="element-283">
            {formData.status === 'Draft' && <>
                <button type="button" onClick={() => setFormData({
              ...formData,
              status: 'Draft'
            })} className="px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-284">
                  Save as Draft
                </button>
                <button type="button" onClick={handleSubmit} disabled={isSubmitting} className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50" data-id="element-285">
                  {isSubmitting && <Loader2 className="animate-spin -ml-1 mr-2 h-4 w-4" data-id="element-286" />}
                  Submit for Approval
                </button>
              </>}
            {formData.status === 'Approved' && <button type="button" onClick={handlePublish} className="px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700" data-id="element-287">
                Publish EOI
              </button>}
          </div>
        </div>
      </div>

      <SupplierViewModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} supplier={selectedSupplier} type="EOI" data-id="element-288" />
    </div>;
}